import type * as React from 'react'
import { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { CellProps, Column } from 'react-table'

import {
  GS_SEARCH_RESULTS_ITEM_DESCRIPTION,
  GS_SEARCH_RESULTS_ITEM_STATUS,
} from 'Modules/searchResults/constants/searchResults.contants'
import { ParsedSearchResults } from 'Modules/searchResults/types/searchResults.types'

import { ImageWithFallback } from 'Components/ImageWithFallback/ImageWithFallback'
import { OrderStatusBadge } from 'Components/OrderStatusBadge/OrderStatusBadge'
import { TableList } from 'Components/TableList/TableList'

import { buttonize } from 'Utilities'

import { E2E_TABLE_LINK_SEARCH_RESULTS } from 'Constants/e2e'
import { PORTAL_CONTENT_MODEL } from 'Constants/portalContentModel'

import { DeviceTypeEnum } from 'Types/deviceTypes'

import { SearchResultDetails } from '../SearchResultsDetails/SearchResultsDetails'
import { SearchResultTitle } from '../SearchResultsTitle/SearchResultsTitle'
import { SearchResultListProps } from './SearchResultsList.types'

export const SearchResultsList = (props: SearchResultListProps) => {
  const { dataSource, isLoading } = props
  const routerHistory = useHistory()

  const handleEnterDetails = useCallback(
    (
      _event:
        | React.MouseEvent<HTMLDivElement>
        | React.KeyboardEvent<HTMLDivElement>,
      url: string,
    ) => {
      routerHistory.push(url)
    },
    [routerHistory],
  )

  const renderNameCell = useCallback(
    (cell: React.PropsWithChildren<CellProps<ParsedSearchResults>>) => {
      const results = cell.row.original

      return (
        <div
          className="d-inline-flex"
          {...buttonize(handleEnterDetails, results.url)}
          data-testid={GS_SEARCH_RESULTS_ITEM_DESCRIPTION}
        >
          <div className="d-flex justify-content-center align-items-center me-4 Table-device-image">
            <ImageWithFallback
              alt={results.document?.['rental.stocklist_item.name']}
              placeholderType={
                (results.document?.[
                  'rental.stocklist_item.product_type'
                ] as DeviceTypeEnum) || DeviceTypeEnum.other
              }
              src={results.document['rental.stocklist_item.image_url'] || ''}
            />
          </div>

          <div className="d-flex flex-column justify-content-center">
            <p className="Table--search-results-category text-muted m-0">
              <FormattedMessage
                id="GlobalSearch_InCategory"
                values={{
                  category: PORTAL_CONTENT_MODEL[results.model]?.translationId,
                }}
              />
            </p>

            <p className="Table--search-results-title">
              <SearchResultTitle
                document={results.document}
                highlights={results.highlights}
                model={results.model}
              />
            </p>

            <dl className="Table--search-results-details Table--search-results-details--is-desktop">
              <SearchResultDetails
                document={results.document}
                highlights={results.highlights}
                model={results.model}
              />
            </dl>
          </div>
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderExpandableContent = useCallback(
    (cell: React.PropsWithChildren<CellProps<ParsedSearchResults>>) => {
      const results = cell.row.original

      return (
        <dl className="Table--search-results-details Table--search-results-details--is-mobile m-0">
          <SearchResultDetails
            document={results.document}
            highlights={results.highlights}
            model={results.model}
          />
        </dl>
      )
    },
    [],
  )

  const renderStatusCell = useCallback(
    (cell: React.PropsWithChildren<CellProps<ParsedSearchResults>>) => {
      const results = cell.row.original
      const caseItemStatus = results.document['case_item.status']

      if (!caseItemStatus) {
        return null
      }

      return (
        <div
          className="d-flex justify-content-center"
          data-testid={GS_SEARCH_RESULTS_ITEM_STATUS}
        >
          <OrderStatusBadge className="fs-6" status={caseItemStatus} />
        </div>
      )
    },
    [],
  )

  const columns = useMemo(
    (): Column<ParsedSearchResults>[] => [
      {
        Cell: renderNameCell,
        ExpandableContent: renderExpandableContent,
        id: 'name',
      },
      {
        Cell: renderStatusCell,
        id: 'status',
      },
    ],
    [renderExpandableContent, renderNameCell, renderStatusCell],
  )

  return (
    <TableList
      className="Table--search-results"
      columns={columns}
      data={dataSource}
      e2eRowId={E2E_TABLE_LINK_SEARCH_RESULTS}
      isLoading={isLoading}
    />
  )
}
